import type { IconName } from "@/lib/icons";
import type { ModeratorCopy } from "@/components/moderator/moderator-copy";
import { testGradingPath } from "@/components/moderator/moderator-navigation";
import { ContractStatus, type Contract } from "@/lib/types/contracts";

/** What waits on the moderator. null: not known (loading or failed). */
export interface TodoCounts {
  tests: number | null;
  contractsToSend: number | null;
  contractsToValidate: number | null;
  payouts: number | null;
}

export interface TodoItem {
  key: "tests" | "contractsToSend" | "contractsToValidate" | "payouts" | "audio";
  title: string;
  hint: string;
  href: string;
  icon: IconName;
  /** null for audio validation, which has no counter, or a count not known yet. */
  count: number | null;
}

/** Signed contracts that only miss the moderator's final validation. */
export function countContractsToValidate(contracts: Contract[]): number {
  return contracts.filter((contract) => Boolean(contract.assignedTo) && contract.status === ContractStatus.SIGNED).length;
}

/** The badge of the menu entry: everything counted, unknowns left out. */
export function totalWaiting(counts: TodoCounts): number {
  return Object.values(counts).reduce<number>((sum, value) => sum + (value ?? 0), 0);
}

const rank = (item: TodoItem) => (item.count !== null && item.count > 0 ? 0 : item.count === null ? 1 : 2);

/**
 * The to-do list, work waiting first: items with something pending keep
 * their usual order at the top, then audio validation (always open), then
 * what is empty.
 */
export function buildTodoItems(lang: string, copy: ModeratorCopy, counts: TodoCounts): TodoItem[] {
  const base = `/${lang}/moderator`;
  const items: TodoItem[] = [
    { key: "tests", title: copy.todoTests, hint: copy.todoTestsHint, href: testGradingPath(lang), icon: "checkCircle2", count: counts.tests },
    { key: "contractsToSend", title: copy.todoContractsToSend, hint: copy.todoContractsToSendHint, href: `${base}/contracts?section=draft`, icon: "send", count: counts.contractsToSend },
    { key: "contractsToValidate", title: copy.todoContractsToValidate, hint: copy.todoContractsToValidateHint, href: `${base}/contracts?section=signed`, icon: "fileText", count: counts.contractsToValidate },
    { key: "payouts", title: copy.todoPayouts, hint: copy.todoPayoutsHint, href: `${base}/payouts`, icon: "wallet", count: counts.payouts },
    { key: "audio", title: copy.todoAudio, hint: copy.todoAudioHint, href: `${base}/validate-audio`, icon: "volume2", count: null },
  ];
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => rank(a.item) - rank(b.item) || a.index - b.index)
    .map(({ item }) => item);
}
